import React, { useState, useEffect } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { Container, AppBar, Toolbar, Typography, Box, BottomNavigation, BottomNavigationAction, Paper, IconButton, Fade } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import BarChartIcon from '@mui/icons-material/BarChart';
import SettingsIcon from '@mui/icons-material/Settings';

import SimpleReport from './components/SimpleReport';
import Settings from './components/Settings';
import UpdateManager from './components/UpdateManager';
import { getCurrentConfig } from './tenant-config';
import { useConfig } from './hooks/useConfig';
import TransactionInput, { TransactionData } from './components/TransactionInput';
import Login from './components/Login';
import InstallPWA from './components/InstallPWA';

const App: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [currentPage, setCurrentPage] = useState<'input' | 'report' | 'settings'>('input');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState('');
  const [tenantConfig, setTenantConfig] = useState(getCurrentConfig());
  const [darkMode, setDarkMode] = useState(false);
  const config = useConfig();

  // Kiểm tra login từ localStorage
  useEffect(() => {
    const loginData = localStorage.getItem('loginData');
    if (loginData) {
      const data = JSON.parse(loginData);
      const loginTime = new Date(data.loginTime);
      const now = new Date();
      const hoursDiff = (now.getTime() - loginTime.getTime()) / (1000 * 60 * 60);

      // Kiểm tra xem đã quá 24 giờ chưa
      if (hoursDiff < 24) {
        const config = getCurrentConfig();
        // Cập nhật tenantName: lấy tên người dùng hoặc local-part của email và thêm " tài chính"
        const baseName: string = typeof data.displayName === 'string' && data.displayName.includes('@')
          ? data.displayName.split('@')[0]
          : (data.displayName || 'Người dùng');
        config.tenantName = `${baseName} tài chính`;
        // Lấy sheetName từ user id đã lưu
        if (data.sheetName) {
          config.sheetName = data.sheetName;
        }
        setTenantConfig(config);
        setCurrentUser(data.displayName);
        setIsLoggedIn(true);
      } else {
        localStorage.removeItem('loginData');
      }
    }
  }, []);

  // Tạo theme theo dark mode và config
  const theme = createTheme({
    palette: {
      mode: darkMode ? 'dark' : 'light',
      primary: {
        main: config.theme?.primaryColor || '#667eea',
      },
      secondary: {
        main: config.theme?.secondaryColor || '#764ba2',
      },
      background: {
        default: darkMode ? '#0a0a0a' : '#f8fafc',
        paper: darkMode ? '#1a1a1a' : '#ffffff',
      },
    },
    shape: {
      borderRadius: 12,
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h6: {
        fontWeight: 600,
      },
    },
    components: {
      MuiAppBar: {
        styleOverrides: {
          root: {
            background: darkMode
              ? 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)'
              : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
    },
  });

  const handleLogin = (config: any) => {
    const tc = getCurrentConfig();
    // Cập nhật tenantName theo yêu cầu: tên người dùng hoặc local-part email + " tài chính"
    const baseName: string = typeof config.displayName === 'string' && config.displayName.includes('@')
      ? config.displayName.split('@')[0]
      : (config.displayName || 'Người dùng');
    tc.tenantName = `${baseName} tài chính`;
    if (config.sheetName) {
      tc.sheetName = config.sheetName;
    }
    setTenantConfig(tc);
    setCurrentUser(config.displayName);
    setIsLoggedIn(true);
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setCurrentUser('');
    setTenantConfig(getCurrentConfig());
    setCurrentPage('input');
  };

  const handleDarkModeChange = (newDarkMode: boolean) => {
    setDarkMode(newDarkMode);
    localStorage.setItem('darkMode', JSON.stringify(newDarkMode));
  };

  const handleTransactionSubmit = async (transaction: TransactionData) => {
    try {
      const response = await fetch(tenantConfig.serverApiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sheetName: tenantConfig.sheetName,
          transaction: transaction
        }),
      });

      if (!response.ok) {
        throw new Error('Lỗi khi lưu dữ liệu');
      }

      await response.json();

    } catch (error) {
      console.error('Error saving transaction:', error);
      throw error;
    }
  };

  const handleMultipleTransactions = async (transactions: TransactionData[]) => {
    setIsLoading(true);
    setStatus('');

    try {
      let successCount = 0;
      let errorCount = 0;

      for (const transaction of transactions) {
        try {
          await handleTransactionSubmit(transaction);
          successCount++;
        } catch (error) {
          errorCount++;
          console.error('Error saving transaction:', transaction, error);
        }
      }

      if (errorCount === 0) {
        setStatus(`Đã lưu thành công ${successCount} giao dịch!`);
      } else if (successCount > 0) {
        setStatus(`Đã lưu ${successCount} giao dịch, ${errorCount} giao dịch lỗi`);
      } else {
        setStatus('Có lỗi xảy ra khi lưu tất cả giao dịch');
      }

      setTimeout(() => setStatus(''), 5000);

    } catch (error) {
      console.error('Error processing transactions:', error);
      setStatus('Có lỗi xảy ra khi xử lý giao dịch');
    } finally {
      setIsLoading(false);
    }
  };

  // Hiển thị màn hình login nếu chưa đăng nhập
  if (!isLoggedIn) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <ThemeProvider theme={theme}>
      <div className="App" style={{
        minHeight: '100vh',
        background: darkMode
          ? 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)'
          : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
      }}>
        <AppBar position="static" elevation={0}>
          <Toolbar sx={{ px: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 700, background: 'linear-gradient(135deg, #ffffff 0%, #f0f0f0 100%)', backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              {config.displayName || currentUser}
            </Typography>
            <IconButton color="inherit" onClick={() => setCurrentPage('settings')} sx={{ background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)', '&:hover': { background: 'rgba(255,255,255,0.2)', transform: 'scale(1.05)' }, transition: 'all 0.2s ease' }}>
              <SettingsIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <Container maxWidth="sm" sx={{ mt: 3, mb: 10, minHeight: 'calc(100vh - 140px)', px: { xs: 2, sm: 3 } }}>
          <Fade in={true} timeout={500}>
            <Box>
              {status && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body1" sx={{ p: 2, borderRadius: 3, background: status.includes('thành công') ? 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)' : 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)', color: 'white', fontWeight: 500, textAlign: 'center', boxShadow: '0 4px 15px rgba(0,0,0,0.1)' }}>
                    {status}
                  </Typography>
                </Box>
              )}
              {currentPage === 'input' && (<TransactionInput tenantConfig={tenantConfig} onSubmit={handleMultipleTransactions} isLoading={isLoading} />)}
              {currentPage === 'report' && (<SimpleReport tenantConfig={tenantConfig} />)}
              {currentPage === 'settings' && (<Settings onLogout={handleLogout} currentUser={currentUser} darkMode={darkMode} onDarkModeChange={handleDarkModeChange} tenantConfig={tenantConfig} />)}
            </Box>
          </Fade>
        </Container>
        <Paper sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1000, background: darkMode ? 'rgba(26, 26, 26, 0.95)' : 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(20px)', borderTop: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}` }} elevation={0}>
          <BottomNavigation value={currentPage} onChange={(event, newValue) => { setCurrentPage(newValue); }} showLabels sx={{ '& .MuiBottomNavigationAction-root': { color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)', '&.Mui-selected': { color: '#667eea' } } }}>
            <BottomNavigationAction label="Nhập" value="input" icon={<AddIcon />} />
            <BottomNavigationAction label="Báo cáo" value="report" icon={<BarChartIcon />} />
          </BottomNavigation>
        </Paper>

        {/* PWA Install Prompt */}
        <InstallPWA onInstall={() => console.log('App installed successfully')} />
        
        {/* PWA Update Manager */}
        <UpdateManager 
          onUpdateAvailable={() => console.log('Update available')}
          onUpdateInstalled={() => console.log('Update installed successfully')}
        />
      </div>
    </ThemeProvider>
  );
};

export default App;
